// Author: Tejal Komb
"use client"

import { useEffect, useState } from "react"
import {
  Heading,
  Box,
  Text,
  Container,
  SimpleGrid,
  Button,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  useBreakpointValue,
  Flex,
  Tag,
  useColorModeValue,
  Icon,
  Divider,
  Spacer
} from "@chakra-ui/react"
import { Briefcase, ArrowRight, Rocket, Users, TrendingUp,Clock, IndianRupee, Code, GraduationCap } from "lucide-react";
import { SearchIcon, TimeIcon, CheckIcon } from "@chakra-ui/icons"
import { FaCode, FaLaptopCode, FaDatabase, FaBrain, FaFlask, FaPencilRuler, FaRupeeSign, FaFileAlt, FaShareAltSquare, FaMobileAlt, FaBullhorn, } from "react-icons/fa"
import axios from "axios"
import { useSelector } from "react-redux"
import Footer from "../components/Footer"
import { Link } from "react-router-dom"
import NavBar from "../components/NavBar"
import backgroundImage from '../images/img1.jpg'
import { motion } from "framer-motion";
import TypingText from "../components/TypingText"; 
import InternshipTracks from "./internship/InternshipTracks";
import { chakra } from "@chakra-ui/react";
import cardPattern from "../images/card-pattern.png";

const MotionBox = motion(chakra.div)
const MotionHeading = motion(Heading)
const MotionText = motion(Text)

const Home = () => {
  const [internships, setInternships] = useState([])
  const [filteredInternships, setFilteredInternships] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [isLoading, setIsLoading] = useState(true)

  const { isAdmin, } = useSelector((state) => state.user)

  const isLargeScreen = useBreakpointValue({ base: false, lg: true })
  const bgColor = useColorModeValue("#f8faf8", "gray.800")
  const cardBg = useColorModeValue("white", "gray.700")
  const textColor = useColorModeValue("gray.700", "gray.100")
  const primaryGreen = "rgba(52, 109, 194, 0.8)"
  const lightGreen = "#e8f5e8"

  useEffect(() => {
    setIsLoading(true)
    axios
      .get(`https://gyanbackend.aim4u.co.in/intern/all-internship/`)
      .then((res) => {
        setInternships(res.data)
        setFilteredInternships(res.data)
        setIsLoading(false)
      })
      .catch((err) => {
        console.log(err)
        setIsLoading(false)
      })
  }, [])

  const filterInternships = (query) => {
    const normalizedQuery = query.trim().toLowerCase()
    const filteredData = internships.filter((internship) => {
      const searchableFields = [
        internship.Internship_title,
        internship.duration_weeks,
        internship.key_skills,
        internship.eligibility_criteria,
      ]

      return searchableFields.some((field) =>
        String(field ?? "").toLowerCase().includes(normalizedQuery),
      )
    })

    setFilteredInternships(filteredData)
  }

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value)
  }

  const handleSearchSubmit = (event) => {
    event.preventDefault()
    filterInternships(searchTerm)
  }

  // Function to get appropriate icon based on internship title
  const getInternshipIcon = (title) => {
    if (title.toLowerCase().includes("full stack")) return FaCode;
    if (title.toLowerCase().includes("frontend")) return FaLaptopCode;
    if (title.toLowerCase().includes("backend")) return FaDatabase;
    if (
      title.toLowerCase().includes("ai") ||
      title.toLowerCase().includes("data analytics") ||
      title.toLowerCase().includes("data")
    )
      return FaBrain;
    if (
      title.toLowerCase().includes("bio") ||
      title.toLowerCase().includes("med") ||
      title.toLowerCase().includes("research") 
    )
      return FaFlask;
    if (title.toLowerCase().includes("ui") || title.toLowerCase().includes("ux"))
      return FaPencilRuler;
    if (title.toLowerCase().includes("content")) return FaFileAlt;
    if (title.toLowerCase().includes("social ")) return FaShareAltSquare;
    if (title.toLowerCase().includes("app ")) return FaMobileAlt;
    if (title.toLowerCase().includes("marketing")) return FaBullhorn;
    return FaCode;
  };

  // Function to split tech stack into array
  const getTechStackArray = (techStack) => {
    if (!techStack) return []
    return techStack.split(",").map((item) => item.trim())
  }

  const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12, delayChildren: 0.2 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

const floatingBadges = [
  { label: "Career Readiness", top: "18%", left: "6%" },
  { label: "Tech Roles", top: "15%", left: "82%" },
  { label: "Career Growth", top: "68%", left: "85%" },
  { label: "Mentorship", top: "42%", left: "78%" },
];

  return (
    <Box
      fontFamily="SF Pro Display, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif"
      minH="100vh"
      bg={bgColor}
    >
      <NavBar isAdmin={isAdmin} />

{/* ================= HERO SECTION — POLISHED ================= */}

<Box
  position="relative"
  minH={{ base: "75vh", md: "85vh" }}
  display="flex"
  alignItems="center"
  overflow="hidden"
>

  {/* Deep blue gradient base */}
  <Box
    position="absolute"
    inset="0"
    bgGradient="linear(to-b, #071a3a, #0b2a6f 40%, #0f3c96)"
  />

  {/* Soft radial glow */}
  <Box
    position="absolute"
    inset="0"
    bg="radial-gradient(circle at 30% 30%, rgba(92,191,80,0.18), transparent 40%)"
  />

  {/* Background image */}
  <Box
    position="absolute"
    inset="0"
    bgImage={`url(${backgroundImage})`}
    bgSize="cover"
    bgPosition="center"
    opacity={0.18}
    mixBlendMode="overlay"
  />

  {/* ===== Floating Badges — Bigger + Icons ===== */}

  {[
    { t: "Career Readiness", icon: Briefcase, top: "18%", left: "6%" },
    { t: "Tech Roles", icon: Rocket, top: "14%", right: "8%" },
    { t: "Mentorship", icon: Users, top: "48%", right: "10%" },
    { t: "Career Growth", icon: TrendingUp, bottom: "18%", right: "6%" },
    { t: "Learn & Grow", icon: GraduationCap, bottom: "16%", left: "10%" },
  ].map((b, i) => {
    const Icon = b.icon;
    return (
      <MotionBox
        key={i}
        position="absolute"
        top={b.top}
        bottom={b.bottom}
        left={b.left}
        right={b.right}
        px={6}
        py={4}
        borderRadius="18px"
        bg="white"
        color="gray.800"
        fontSize="md"
        fontWeight="800"
        boxShadow="0 14px 40px rgba(0,0,0,0.35)"
        display={{ base: "none", lg: "flex" }}
        alignItems="center"
        gap={3}
        animate={{ y: [0, -16, 0], scale: [1, 1.05, 1] }}
        transition={{ duration: 6 + i, repeat: Infinity }}
      >
        <motion.div
          animate={{ rotate: [0, 12, -12, 0] }}
          transition={{ duration: 4, repeat: Infinity }}
        >
          <Icon size={22} color="#5cbf50" />
        </motion.div>

        {b.t}
      </MotionBox>
    );
  })}

  {/* ===== Main Content ===== */}

  <MotionBox
    position="relative"
    zIndex={2}
    maxW="5xl"
    mx="auto"
    px={6}
    textAlign="center"
    color="white"
    variants={containerVariants}
    initial="hidden"
    animate="visible"
    fontFamily="Poppins, sans-serif"
  >

    {/* Heading line 1 */}
    <MotionHeading
      variants={fadeUp}
      fontSize={{ base: "3xl", md: "5xl", lg: "6xl" }}
      fontWeight="900"
      mb={1}
    >
      Find Your
    </MotionHeading>

    {/* Heading line 2 */}
    <MotionHeading
      variants={fadeUp}
      fontSize={{ base: "3xl", md: "5xl", lg: "6xl" }}
      fontWeight="900"
      mb={6}
      lineHeight="1.05"
    >
      <Text
        as="span"
        bgGradient="linear(to-r, #5cbf50, #3aa0ff)"
        bgClip="text"
      >
        <TypingText />
      </Text>
    </MotionHeading>

    {/* Subtitle */}
    <MotionText
      variants={fadeUp}
      fontSize={{ base: "md", md: "xl" }}
      maxW="3xl"
      mx="auto"
      mb={10}
      color="whiteAlpha.900"
      lineHeight="1.8"
    >
      Find an internship track that matches your interests.{" "}
      <Text as="span" fontWeight="700" color="#5cbf50">
        Opportunities and activities vary by track.
      </Text>
    </MotionText>

    {/* ===== Bigger Search Bar ===== */}

    <MotionBox as="form" variants={fadeUp} onSubmit={handleSearchSubmit}>
      <InputGroup
        w="full"
        maxW="4xl"
        mx="auto"
        bg="white"
        borderRadius={{ base: "18px", md: "22px" }}
        boxShadow="0 25px 70px rgba(0,0,0,0.35)"
        h={{ base: "64px", md: "78px" }}
      >
        <InputLeftElement
          pointerEvents="none"
          h="full"
          w={{ base: "12", md: "14" }}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <SearchIcon color="#5cbf50" />
        </InputLeftElement>

        <Input
          value={searchTerm}
          onChange={handleSearchChange}
          aria-label="Search internships by title, skills, eligibility, or duration"
          placeholder="Search by title, tech stack, duration..."
          h="full"
          minW={0}
          pl={{ base: "12", md: "14" }}
          pr={{ base: "24", md: "32" }}
          fontSize={{ base: "sm", sm: "md", md: "lg" }}
          fontWeight="500"
          border="none"
          color="gray.700"
          borderRadius={{ base: "18px", md: "22px" }}
          _placeholder={{ color: "gray.500" }}
          _focus={{ boxShadow: "none" }}
        />

        <InputRightElement
          h="full"
          w="auto"
          right={{ base: "2", md: "3" }}
          display="flex"
          alignItems="center"
          pointerEvents="auto"
        >
          <Button
            type="submit"
            h={{ base: "44px", md: "56px" }}
            minW={{ base: "84px", md: "112px" }}
            bg="#5cbf50"
            color="white"
            borderRadius={{ base: "14px", md: "16px" }}
            px={{ base: 4, md: 6 }}
            fontSize={{ base: "sm", md: "lg" }}
            fontWeight="700"
            _hover={{ bg: "#4aaa42" }}
          >
            Search
          </Button>
        </InputRightElement>
      </InputGroup>
    </MotionBox>

  </MotionBox>
</Box>

      {/* Internship Listings */}
      <Container maxW="1400px" py={8} px={{ base: 4, md: 6 }}>
        <Box mb={8}>
          <Heading size="2xl" mb={3} color="#3c8c30" fontWeight="bold">
            Available Internships
          </Heading>
          <Text color={textColor} fontSize="xl">
            Discover opportunities that match your skills and interests
          </Text>
          <Divider mt={4} borderColor="gray.200" />
        </Box>

        {isLoading ? (
          <Flex justify="center" align="center" height="300px">
            <Text fontSize="lg">Loading internships...</Text>
          </Flex>
        ) : (
          <SimpleGrid minChildWidth="280px" spacing={{ base: 4, md: 5 }} alignItems="stretch">
            {filteredInternships.map((internship, index) => {
              const InternshipIcon = getInternshipIcon(internship.Internship_title)
              const techStackArray = getTechStackArray(internship.key_skills)

              return (
      <MotionBox
  key={internship.id}
  position="relative"
  h="100%"
  minH={{ base: "390px", md: "410px" }}
  borderRadius="24px"
  overflow="hidden"
 bgGradient={
  index % 3 === 0
    ? "linear(to-br, hsl(var(--brand-blue) / 0.95), hsl(var(--brand-blue-light) / 0.95), hsl(var(--brand-blue-dark) / 1))"
    : index % 3 === 1
    ? "linear(to-br, rgba(10, 167, 62, 0.95), rgb(10, 178, 55), rgba(25, 9, 168, 0.95))"
    : "linear(to-br, rgba(4, 53, 97, 0.95), rgba(35, 60, 141, 0.95), rgba(30,58,138,0.95))"
  }
  boxShadow="0 25px 70px -20px rgba(0,0,0,0.65)"
  initial={{ opacity: 0, y: 30 }}
  animate={{ opacity: 1, y: 0 }}
  whileHover={{ y: -8 }}
  transition={{ duration: 0.5, delay: index * 0.08 }}
>

  {/* pattern overlay */}
  <Box
    position="absolute"
    inset="0"
    opacity="0.10"
    bgImage={`url(${cardPattern})`}
    bgSize="180px"
    pointerEvents="none"
  />

  {/* glow blob */}
  <Box
    position="absolute"
    top="-60px"
    right="-60px"
    w="160px"
    h="160px"
    borderRadius="full"
    bg="#5cbf50"
    opacity="0.18"
    filter="blur(60px)"
  />

  <Flex direction="column" h="100%" p={{ base: 5, md: 6 }} color="white">

    {/* icon badge */}
    <MotionBox
      whileHover={{ rotate: 5, scale: 1.05 }}
      w="56px"
      h="56px"
      borderRadius="18px"
      bg="whiteAlpha.200"
      backdropFilter="blur(10px)"
      border="1px solid"
      borderColor="whiteAlpha.300"
      display="flex"
      alignItems="center"
      justifyContent="center"
      mb={4}
    >
      <Icon as={InternshipIcon} boxSize={6} />
    </MotionBox>

    {/* title */}
    <Heading size="md" noOfLines={2} mb={2} color="white">
      {internship.Internship_title}
    </Heading>

    {/* eligibility */}
    <Flex align="center" gap={2} mt={2}>
      <GraduationCap size={16} opacity={0.6} />
      <Text fontSize="xs" fontWeight="bold" opacity={0.6}>
        ELIGIBILITY
      </Text>
    </Flex>

    <Text fontSize="sm" noOfLines={2} opacity={0.9} mb={4}>
      {internship.eligibility_criteria || "See internship details for eligibility"}
    </Text>

    {/* tech stack */}
    <Flex align="center" gap={2} mb={2}>
      <Code size={16} opacity={0.6} />
      <Text fontSize="xs" fontWeight="bold" opacity={0.6}>
        TECH STACK
      </Text>
    </Flex>

    <Flex wrap="wrap" gap={2} mb={4}>
      {techStackArray.slice(0,5).map((tech, idx) => (
        <MotionBox
          key={idx}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.25 + idx * 0.05 }}
          px={3}
          py={1}
          fontSize="xs"
          borderRadius="full"
          bg="whiteAlpha.200"
          border="1px solid"
          borderColor="whiteAlpha.300"
        >
          {tech}
        </MotionBox>
      ))}
    </Flex>

    <Spacer />

    {/* bottom row */}
    <Flex justify="space-between" mb={4}>

      <Box>
        <Text fontSize="xs" opacity={0.6}>Duration</Text>
        <Flex align="center" gap={2}>
          <Clock size={16} />
          <Text fontWeight="bold">
            {internship.duration_weeks} weeks
          </Text>
        </Flex>
      </Box>

      <Box textAlign="right">
        <Text fontSize="xs" opacity={0.6}>FEES</Text>
        <Flex
          align="center"
          gap={1}
          px={3}
          py={1}
          borderRadius="md"
          bg="whiteAlpha.200"
          border="1px solid"
          borderColor="whiteAlpha.300"
        >
          <IndianRupee size={14} />
          <Text fontWeight="bold">
            {internship.fees}
          </Text>
        </Flex>
      </Box>

    </Flex>

    {/* CTA */}
    <Link to={`/details/${internship.id}`} style={{ width: "100%" }}>
      <MotionBox whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
        <Button
          w="100%"
          bg="white"
          color="black"
          _hover={{ bg: "gray.100" }}
          rightIcon={<ArrowRight size={16} />}
          borderRadius="14px"
        >
          View Details
        </Button>
      </MotionBox>
    </Link>

  </Flex>
</MotionBox>
              )
            })}
          </SimpleGrid>
        )}

        {!isLoading && filteredInternships.length === 0 && (
          <Box textAlign="center" py={10} bg={cardBg} borderRadius="lg" boxShadow="sm" p={8}>
            <Text fontSize="xl" mb={4} color={textColor}>
              No internships found matching your search.
            </Text>
            <Button
              onClick={() => {
                setSearchTerm("")
                setFilteredInternships(internships)
              }}
              bg={primaryGreen}
              color="white"
              _hover={{ bg: "#4da04d" }}
              size="lg"
              px={8}
            >
              Clear Search
            </Button>
          </Box>
        )}
      </Container>
<InternshipTracks />  

      <Footer />

      {/* Add custom scrollbar styles */}
      <style jsx global>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: ${lightGreen};
                    border-radius: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: ${primaryGreen};
                    border-radius: 4px;
                }
                .custom-scrollbar {
                    scrollbar-width: thin;
                    scrollbar-color: ${primaryGreen} ${lightGreen};
                }
            `}</style>
    </Box>
  )
}

export default Home
